import { Component, effect, inject } from "@angular/core";
import { ThousandizePipe } from "ev-core";
import { EvLogoLoaderComponent } from "src/app/shared-components/ev-logo-loader/ev-logo-loader.component";
import { EvTooltipModule } from "ev-tooltip";
import { EvArrowChoiceComponent } from "src/app/shared-components/ev-arrow-choice/ev-arrow-choice.component";
import { DemographicAreaData, StatisticPanel } from "src/models/map/demographic-area-data";
import { EVEstateType } from "src/models/units/unit-attributes.model";
import { DemographyDataService, VisibleSection } from "src/services/estate/demography-data.service";
import { BuyerOriginComponent, GraphValue } from "./buyer-origin/buyer-origin.component";
import { SingleValue, SingleValueComponent } from "./single-value/single-value.component";
import { StatisticsPanelComponent } from "./statistics-panel/statistics-panel.component";

@Component({
    selector: "buyer-demography",
    imports: [EvLogoLoaderComponent, ThousandizePipe, SingleValueComponent, BuyerOriginComponent, EvTooltipModule, EvArrowChoiceComponent, StatisticsPanelComponent],
    templateUrl: "./buyer-demography.component.html",
    styleUrl: "./buyer-demography.component.scss",
})
export class BuyerDemographyComponent {
    demographicsDataService = inject(DemographyDataService);
    statisticsPanels: StatisticPanel[] = [];
    visibleSection = VisibleSection;
    useLastThreeYears: boolean = false;
    showMoreInfo: boolean = false;
    showNumbers: boolean;
    showBuyers: boolean = false;
    demography: DemographicAreaData = null;
    matrixColumns: IMatrixColumn[] = [];
    graphData: Array<GraphValue> = [];
    buyerOrigin: any[] = [];
    estateTypesFilter: boolean[] = [false, false, true, true, true, false, true, true];

    //tooltip
    ownerAgeInfoTooltip: DemographyTooltip;
    buyerAgeInfoTooltip: DemographyTooltip;
    ownedPeriodNowTooltip: DemographyTooltip;
    ownedPeriodPrevTooltip: DemographyTooltip;
    ownerNotOnAddressShareTooltip: DemographyTooltip;
    singleOwnerShareTooltip: DemographyTooltip;
    changeMatrixTooltip: DemographyTooltip;
    firstTimeBuyersShareTooltip: DemographyTooltip;
    warningOwnedPeriodPrevTooltip: DemographyTooltip;
    warningOwnedPeriodNowTooltip: DemographyTooltip;

    allEstateHeader: { header: string[] };
    lastYearsEstateHeader: { header: string[] };
    demographicsData: DemographicAreaData;

    graphTotalNum: number;
    graphInnerCityShare: number;
    graphInnerCouncilShare: number;

    ownerNotOnAddress: SingleValue;
    singleOwner: SingleValue;
    firstTimeBuyers: SingleValue;

    constructor() {
        effect(() => {
            if (this.demographicsDataService.demographicsIsLoading()) {
                this.demographicsData = null;
            }

            if (this.demographicsDataService.demographicResource.hasValue()) {
                this.demographicsData = this.demographicsDataService.demographicResource.value();
                this.setupTooltipTexts();
                this.newDemographics(this.demographicsData)
            }
        });
    }

    changeUseLastThreeYears(): void {
        this.useLastThreeYears = !this.useLastThreeYears;
        if (this.useLastThreeYears) {
            this.demographicsDataService.pursuantTransitionYears.set(3);
        } else {
            this.demographicsDataService.pursuantTransitionYears.set(null);
        }
    }

       matrixDataDenominatorChanged(index: number): void {
        if (index === 0) this.showNumbers = false;
        //Percent
        else if (index === 1) this.showNumbers = true;
        //Values
        else throw "Option not handled";
    }

    private readonly newDemographics = (data: DemographicAreaData) => {
        if (data != null) {
            this.resetViewData();
            this.showBuyers = false;
            this.toViewModel(data);
        } else {
            this.resetViewData();
        }
    };

    private setupTooltipTexts() {
        this.ownerAgeInfoTooltip = {
            show: false,
            header: "Hvor gamle er eierne?",
            info: [
                "Vi beregner alder for alle eksisterende hjemmelshavere som er registret med fødselsnummer, uavhengig av hvor stor eierandel de har. " +
                    "NB! Dette er ikke et nøyaktig tall på hvor gamle de som bor i området er. Siden vi kun har tilgang på hjemmelshavere vet vi " +
                    "ikke om disse bor på eiendommen. Det store tallet er gjennomsnittsalder. Grunnlaget er antall eiere. ",
                "Eksempel: A og B kjøper en bolig sammen og tinglyser 50% eierandel hver. B flytter ut og selger sin andel tilbake til A. " +
                    "C flytter så inn og tinglyser en 30% eierandel som er kjøpt fra A. A og C eier boligen i dag. " +
                    "I dette tilfellet er det alder på A og C i dag som telles, og de teller like mye.",
            ],
        };

        this.buyerAgeInfoTooltip = {
            show: false,
            header: "Hvor gamle er kjøperne?",
            info: [
                "Vi beregner tallene for alle hjemmelshavere som er registret med fødselsnummer, og kun på eiendommer der det er registrert " +
                    "hjemmelsoverganger med 100% eierskifte. Det vil si at hjemmelshavere som kjøper seg inn i en eiendom fjernes fra statistikken." +
                    " Hjemmelshavere som senere har solgt seg ut av en andel i boligen vil være inkludert i tallene. " +
                    "For borettslag er alle hjemmelshavere som er tinglyst i 2006 og 2007 fjernet fra statistikken. " +
                    "Dette er i de fleste tilfeller ikke reelt kjøpstidspunkt, men resultat av masseregistrering i forbindelse med opprettelse " +
                    "av Borettslagsregisteret. Det store tallet er gjennomsnittsalder. Grunnlaget er antall eiere som oppfyller kriteriene.",
                "Eksempel: A og B kjøper en bolig sammen og tinglyser 50% eierandel hver. B flytter ut og selger sin andel tilbake til A. " +
                    "C flytter så inn og tinglyser en 30% eierandel som er kjøpt fra A. A og C eier boligen i dag. " +
                    "I dette tilfellet er det alder på A og B ved tinglyst hjemmelsovergang som telles.",
            ],
        };

        this.ownedPeriodNowTooltip = {
            show: false,
            header: "Hvor lenge har dagens eiere eid boligen?",
            info: [
                "Vi beregner tallene for alle eksisterende hjemmelshavere som er registret med fødselsnummer, og kun på eiendommer " +
                    "der det er registrert hjemmelsoverganger med 100% eierskifte og at de fortsatt eier eiendommen. " +
                    "Det vil si at hjemmelshavere som kjøper seg inn i, eller selger seg ut av en eiendom, fjernes fra statistikken. " +
                    "Tallene inneholder kun selveide boliger. Vi har ikke data på hvor lenge eiere av borettslagsandeler har eid boligen, " +
                    "da borettslagregisteret først ble opprettet i 2007. Det store tallet er gjennomsnittlig eietid. " +
                    "Grunnlaget er antall eiere som oppfyller kriteriene.",
                "Eksempel: A og B kjøper en bolig sammen og tinglyser 50% eierandel hver. B flytter ut og selger sin andel tilbake til A. " +
                    "C flytter så inn og tinglyser en 30% eierandel som er kjøpt fra A. A og C eier boligen i dag. " +
                    "I dette tilfellet er det kun hvor lenge A har eid boligen som telles.",
            ],
        };

        this.ownedPeriodPrevTooltip = {
            show: false,
            header: "Hvor lenge eier man før man selger?",
            info: [
                "Vi beregner tallene for alle hjemmelshavere som er registret med fødselsnummer, og kun på eiendommer der det er " +
                    "registrert hjemmelsoverganger med 100% eierskifte, både på kjøp og salgstidspunktet. Det vil si at " +
                    "hjemmelshavere som kjøper seg inn i, eller selger seg ut av en eiendom fjernes fra statistikken. " +
                    "Tallene inneholder kun selveide boliger. Vi har ikke data på hvor lenge eiere av borettslagsandeler " +
                    "har eid boligen, da borettslagregisteret først ble opprettet i 2007. Da dagens eiere naturlig nok ikke har " +
                    "flyttet enda ser vi på de forrige eierne av de samme boligene. Det store tallet er gjennomsnittlig eietid. " +
                    "Grunnlaget er antall eiere som oppfyller kriteriene.",
                "Eksempel: A og B kjøper en bolig sammen og tinglyser 50% eierandel hver. B flytter ut og selger sin andel tilbake til A. " +
                    "C flytter så inn og tinglyser en 30% eierandel som er kjøpt fra A. A og C selger boligen og flytter inn i ny. " +
                    "I dette tilfellet er det kun A som telles i tallene for hvor lenge man eide boligen.",
            ],
        };

        this.ownerNotOnAddressShareTooltip = {
            show: false,
            header: "Hvor mange bor et annet sted enn boligen?",
            info: [
                "Hvis hjemmelshaver har en annen adresse i folkeregisteret enn boligen har, antar vi at eier ikke bor på adressen. " +
                    "I tallene kan det derfor finnes f.eks. utleieobjekter, barn som bor i foreldres leilighet, pendlerleiligheter mm. " +
                    "Grunnlaget er antall boliger.",
            ],
        };

        this.singleOwnerShareTooltip = {
            show: false,
            header: "Hvor stor andel singeleiere er det?",
            info: ["Singeleier betyr ikke at man nødvendigvis bor alene i boligen, men at man er den eneste hjemmelshaver. Grunnlaget er antall boliger."],
        };

        this.changeMatrixTooltip = {
            show: false,
            header: "Hvilken boligtype kommer kjøperne fra? ",
            info: [
                "Tallene er basert på boligtypen til boligen som eier eide inntil 6 måneder før, eller solgte innen 6 måneder etter " +
                    "hjemmelsovergang til denne. Eiere som eide flere eiendommer er tatt ut av statistikken. " +
                    "Tallene gjelder den enkelte hjemmelshaver der hjemmelsovergangen gjelder 100% av boligen. " +
                    "I tallene for «?» er det ikke registrert noen bolig på dagens eier innen 6 måneder før hjemmelsovergangen. " +
                    "Dersom eier solgte en borettslagsandel før 2007, har vi imidlertid ikke data om dette. " +
                    "Tinglysning av hjemmelshavere på borettslagsandeler startet i 2007. Eier kan også ha eid Aksje- eller " +
                    "obligasjonsleiligheter, da det ikke finnes noen registre for disse eieformene. Grunnlaget er antall eiere som oppfyller kriteriene.",
                "Eksempel: A og B kjøper en bolig sammen og tinglyser 50% eierandel hver. B flytter ut og selger sin andel tilbake til A. " +
                    "C flytter så inn og tinglyser en 30% eierandel som er kjøpt fra A. A og C eier boligen i dag. " +
                    "I dette tilfellet er det A og B som telles i tallene for endring av boligtype dersom A og B eide bolig fra før (innen +/-6 måneder). " +
                    "Dette er uavhengig av om de eide samme bolig eller om de eide en hver.",
                "Du kan velge mellom å se prosentandeler eller antall ved å klikke på henholdsvis % eller Antall",
            ],
        };

        this.firstTimeBuyersShareTooltip = {
            show: false,
            header: "Har dagens eier eid bolig tidligere?",
            info: [
                "Tallene baserer seg på om det er registret boliger på dagens eier tidligere. Dersom eier solgte en borettslagsandel før 2007, " +
                    "har vi imidlertid ikke data om dette. Tinglysning av hjemmelshavere på borettslagsandeler startet i 2007. " +
                    "Eier kan også ha eid Aksje- eller obligasjonsleiligheter, da det ikke finnes noen registre for disse eieformene. " +
                    "Grunnlaget er antall eiere som eier boligen i dag.",
            ],
        };

        this.warningOwnedPeriodPrevTooltip = {
            show: false,
            header: "",
            info: [
                "Utvalget inneholder borettslagsandeler med eiertid fra før disse ble tinglyst i grunnboka fra 2007." +
                    "Vi kjenner ikke eiertiden til disse, og de er derfor ikke inkludert i beregningen.",
            ],
        };

        this.warningOwnedPeriodNowTooltip = {
            show: false,
            header: "",
            info: [
                "Utvalget inneholder borettslagsandeler med eiertid fra før disse ble tinglyst i grunnboka fra 2007." +
                    "Vi kjenner ikke eiertiden til disse, og de er derfor ikke inkludert i beregningen.",
            ],
        };
    }
    private setupHeaderText() {
        this.allEstateHeader = {
            header: [
                "Basert på solgte selveide boliger og borettslagsandeler. Fritidsboliger er ikke inkludert. Basert på valg i filter, unntatt tidsperiode.",
            ],
        };
        this.lastYearsEstateHeader = {
            header: [
                "Basert på solgte selveide boliger og borettslagsandeler. Fritidsboliger ikke inkludert. " +
                    "Basert på valg i filter, unntatt tidsperiode. Viser kun registrerte hjemmelsoverganger med 100% eierskifte siste 3 år.",
            ],
        };
    }
    private setupVacationHomesHeaderText() {
        this.allEstateHeader = {
            header: ["Basert på solgte fritidsboliger. Selveide boliger og borettslagsandeler ikke inkludert. Basert på valg i filter, unntatt tidsperiode."],
        };
        this.lastYearsEstateHeader = {
            header: [
                "Basert på solgte fritidsboliger. Selveide boliger og borettslagsandeler ikke inkludert. Basert på valg i filter, unntatt tidsperiode. " +
                    "Viser kun registrerte hjemmelsoverganger med 100% eierskifte siste 3 år.",
            ],
        };
    }

    private toViewModel(data: DemographicAreaData) {
        this.demography = { ...data };

        this.demography.ownerNotOnAddressShare = this.round(this.demography.ownerNotOnAddressShare);
        this.demography.firstTimeBuyersShare = this.round(this.demography.firstTimeBuyersShare);
        this.demography.singleOwnerShare = this.round(this.demography.singleOwnerShare);
        this.demography.movedWithinCityDistrictShare = this.round(this.demography.movedWithinCityDistrictShare);
        this.demography.movedWithinCouncilShare = this.round(this.demography.movedWithinCouncilShare);
        this.demography.movedWithinCountyShare = this.round(this.demography.movedWithinCountyShare);

        this.buyerOrigin = this.demography.buyersOrigin ? JSON.parse(this.demography.buyersOrigin) : [];
        
        this.graphTotalNum = this.demography.movedWithinNum;
        this.ownerNotOnAddress = {
            text: "Eiere med annen adresse enn boligen",
            share: this.demography.ownerNotOnAddressShare,
            num: this.demography.ownerNotOnAddressShareNum,
        };
        this.singleOwner = { text: "Boliger eid av 1 person", share: this.demography.singleOwnerShare, num: this.demography.singleOwnerShareNum };
        this.firstTimeBuyers = { text: "Førstegangs-kjøpere", share: this.demography.firstTimeBuyersShare, num: this.demography.firstTimeBuyersShareNum };

        this.graphInnerCityShare = (this.demography.movedWithinCityDistrictShare / this.demography.movedWithinCountyShare) * 100;
        this.graphInnerCouncilShare = (this.demography.movedWithinCouncilShare / this.demography.movedWithinCountyShare) * 100;

        this.demography.ownerNotOnAddressShareNum = this.round(this.demography.ownerNotOnAddressShareNum);
        this.demography.firstTimeBuyersShareNum = this.round(this.demography.firstTimeBuyersShareNum);
        this.demography.singleOwnerShareNum = this.round(this.demography.singleOwnerShareNum);

        this.makestatisticsPanels(data);
        this.makeMatrixColumns(data);
        if (data.isOnlyVacationHomes) {
            this.setupVacationHomesHeaderText();
        } else {
            this.setupHeaderText();
        }
    }

    private makestatisticsPanels(data: DemographicAreaData) {
        this.statisticsPanels.splice(0);
        this.statisticsPanels.push(this.initializePanel(data.buyerAge, "Alder på eiere", "Ved kjøp", this.buyerAgeInfoTooltip));
        this.statisticsPanels.push(this.initializePanel(data.ownerAge, "Alder på eiere", "I dag", this.ownerAgeInfoTooltip));
        this.statisticsPanels.push(
            this.initializePanel(data.ownedPeriodNow, "År eid bolig", "Dagens eiere", this.ownedPeriodNowTooltip, this.warningOwnedPeriodNowTooltip),
        );
        this.statisticsPanels.push(
            this.initializePanel(data.ownedPeriodPrev, "År eid bolig", "Forrige eier", this.ownedPeriodPrevTooltip, this.warningOwnedPeriodPrevTooltip),
        );
    }

    private initializePanel(panel: StatisticPanel, title: string, subTitle: string, tooltip: DemographyTooltip, warningTooltip?: DemographyTooltip): StatisticPanel {
        panel = { ...panel };
        panel.headerTxt = title;
        panel.subTxt = subTitle;
        panel.tooltip = tooltip;
        panel.warningTooltip = warningTooltip;
        return panel;
    }

     private makeMatrixColumns(data: DemographicAreaData) {
        this.matrixColumns.splice(0);

        if (this.estateTypesFilter[EVEstateType.Enebolig] && data.estateTypeChangesDetachedNum) {
            const numberOf = data.estateTypeChangesDetachedNum;
            const nonZero = numberOf?.find((p) => p > 0);

            if (nonZero) {
                this.matrixColumns.push({
                    type: "Enebolig",
                    percent: data.estateTypeChangesDetached,
                    number: numberOf
                });
            }
        }

        if (this.estateTypesFilter[EVEstateType.Tomannsbolig] && data.estateTypeChangesSemiDetachedNum) {
            const numberOf = data.estateTypeChangesSemiDetachedNum;
            const nonZero = numberOf?.find((p) => p > 0);

            if (nonZero) {
                this.matrixColumns.push({
                    type: "Tomannsbolig",
                    displayName: "Tomanns",
                    percent: data.estateTypeChangesSemiDetached,
                    number: numberOf
                });
            }
        }

        if (this.estateTypesFilter[EVEstateType.Rekkehus] && data.estateTypeChangesRowhouseNum) {
            const numberOf = data.estateTypeChangesRowhouseNum;
            const nonZero = numberOf?.find((p) => p > 0);

            if (nonZero) {
                this.matrixColumns.push({
                    type: "Rekkehus",
                    percent: data.estateTypeChangesRowhouse,
                    number: numberOf
                });
            }
        }

        if (this.estateTypesFilter[EVEstateType.Leilighet] && data.estateTypeChangesFlatNum) {
            const numberOf = data.estateTypeChangesFlatNum;
            const nonZero = numberOf?.find((p) => p > 0);

            if (nonZero) {
                this.matrixColumns.push({
                    type: "Leilighet",
                    percent: data.estateTypeChangesFlat,
                    number: numberOf
                });
            }
        }
    }

    private round(n: number): number {
        return n < 1 ? Math.round(n * 10) / 10 : Math.round(n);
    }

    private resetViewData() {
        this.graphData = [];
        this.demography = null;
        this.statisticsPanels = [];
        this.matrixColumns = [];
        this.estateTypesFilter = [false, false, true, true, true, false, true, true];
    }
}

export interface DemographyTooltip {
    show: boolean;
    header: string;
    info: string[];
}

interface IMatrixColumn {
    type: string;
    displayName?: string;
    percent: number[];
    number: number[];
}
